import { _isNil, _isNotBlank } from '@/littledash';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import DeviceEventBus, { type DeviceNewReadingDetail, type DeviceType } from '@/utils/devices/DeviceEventBus';
import { isCancel } from 'axios';
import { catchError, concatMap, EMPTY, from, Subject, takeUntil, tap } from 'rxjs';
import type { DataTableColumn, DataTableService } from '../DataTable.model';
import {
  deviceColumnNotInWorkflowToast,
  deviceNoCellSelectedToast,
  deviceWriteFailedToast,
  deviceWriteInvalidColumnToast,
  deviceWriteLockedColumnToast,
  deviceWriteSuccessToast,
} from './DataTableDeviceToast';

const EXCLUDED_TARGETS: ReadonlySet<string> = new Set(['focus-search', 'assign-id']);

type QueuedReading = {
  value: string;
  deviceTitle: string;
  deviceType: DeviceType | undefined;
};

type ColumnRejectionReason = 'locked' | 'invalid-type';

/**
 * Subscribes to DeviceEventBus and writes accepted device readings into the
 * currently selected datatable cell, then auto-advances selection. Readings
 * are serialized via RxJS concatMap so each completes before the next begins.
 */
export class DataTableDeviceListener {
  readonly #abortController = new AbortController();
  readonly #destroy$ = new Subject<void>();
  readonly #readingQueue$ = new Subject<QueuedReading>();
  readonly #dataTableService: DataTableService;

  constructor(dataTableService: DataTableService) {
    this.#dataTableService = dataTableService;

    this.#readingQueue$
      .pipe(
        takeUntil(this.#destroy$),
        concatMap((reading) => this.#processReading(reading))
      )
      .subscribe();

    DeviceEventBus.subscribe('device:new-reading', this.#handleReading, {
      signal: this.#abortController.signal,
    });
  }

  destroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
    this.#abortController.abort();
  }

  readonly #handleReading = (event: CustomEvent<DeviceNewReadingDetail>): void => {
    if (document.hidden) {
      return;
    }

    if (EXCLUDED_TARGETS.has(event.detail.target)) {
      return;
    }

    event.preventDefault();
    this.#readingQueue$.next({
      value: event.detail.value,
      deviceTitle: event.detail.device.title,
      deviceType: event.detail.device.type,
    });
  };

  #processReading(reading: QueuedReading) {
    const selection = this.#dataTableService.selection;
    if (_isNil(selection)) {
      deviceNoCellSelectedToast(reading.deviceTitle, reading.deviceType);
      return EMPTY;
    }

    const { column: columnIndex, row: rowIndex } = selection.topLeft;
    const column = this.#dataTableService.columnByIndex(columnIndex);
    if (_isNil(column)) {
      ExceptionHandler.captureException(
        new InVivoError('Device reading dropped: columnByIndex returned undefined', {
          slug: 'datatable-device-reading-column-resolution',
          context: { column_index: `${columnIndex}` },
        })
      );
      deviceWriteFailedToast(reading.deviceTitle, reading.deviceType);
      return EMPTY;
    }

    const rejectionReason = this.#columnRejectionReason(column);
    if (rejectionReason === 'locked') {
      deviceWriteLockedColumnToast(reading.deviceTitle, reading.deviceType);
      return EMPTY;
    }
    if (rejectionReason === 'invalid-type') {
      deviceWriteInvalidColumnToast(reading.deviceTitle, reading.deviceType);
      return EMPTY;
    }

    const workflowService = this.#dataTableService.workflowService;
    const inWorkflow = workflowService.workflowActive && workflowService.sourceInWorkflow(column.id);

    if (workflowService.workflowActive && !inWorkflow) {
      deviceColumnNotInWorkflowToast(reading.deviceTitle, reading.deviceType);
      return EMPTY;
    }

    const idCoordinate = this.#dataTableService.fromIndexCoordinate({ column: columnIndex, row: rowIndex });
    if (_isNil(idCoordinate)) {
      ExceptionHandler.captureException(
        new InVivoError('Device reading dropped: fromIndexCoordinate returned undefined', {
          slug: 'datatable-device-reading-coordinate-resolution',
          context: { column_index: `${columnIndex}`, row_index: `${rowIndex}` },
        })
      );
      return EMPTY;
    }

    return from(
      this.#dataTableService.updateCells([
        { column_id: idCoordinate.column, row_id: idCoordinate.row, value: reading.value },
      ])
    ).pipe(
      tap(() => {
        if (this.#abortController.signal.aborted) {
          return;
        }
        this.#dataTableService.validate();
        this.#dataTableService.flashCell(idCoordinate, 'green');
        deviceWriteSuccessToast(reading.deviceTitle, this.#successValueLine(column, reading.value), reading.deviceType);
        if (inWorkflow) {
          workflowService.workflowActionExit(column.id);
        } else {
          this.#dataTableService.moveSelection('DOWN');
        }
      }),
      catchError((err) => {
        if (isCancel(err)) {
          return EMPTY;
        }
        ExceptionHandler.captureException(
          new InVivoError('Failed to apply device reading update', {
            cause: err,
            slug: 'datatable-device-reading-update-error',
            context: { column_id: idCoordinate.column, row_id: idCoordinate.row },
          })
        );
        deviceWriteFailedToast(reading.deviceTitle, reading.deviceType);
        return EMPTY;
      })
    );
  }

  #successValueLine(column: DataTableColumn, value: string): string {
    if ('unit' in column && _isNotBlank(column.unit)) {
      return `${value} ${column.unit}`;
    }
    return value;
  }

  #columnRejectionReason(column: DataTableColumn): ColumnRejectionReason | null {
    if (column.read_only || column.locked) {
      return 'locked';
    }
    return this.#isNumericWritableColumn(column) ? null : 'invalid-type';
  }

  #isNumericWritableColumn(column: DataTableColumn): boolean {
    if (column.type === 'number' || column.type === 'sampleDetail') {
      return true;
    }
    if (column.type === 'measurement') {
      return !column.measurement.is_calculated;
    }
    if (column.type === 'metadata') {
      return column.metadata?.field_type === 'numeric';
    }
    return false;
  }
}
